/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ImportAuthorizationCodesModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Class of Services module.
----------------------------------------------------------------*/

import { useEffect, useRef, useState } from 'react';
import { Upload, X, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { buildApiUrl, getApiHeaders } from '../../config/api';

interface ImportAuthorizationCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tenantSlug: string;
  domainId: number | string | null;
}

interface ImportResults {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export default function ImportAuthorizationCodesModal({ isOpen, onClose, onSuccess, tenantSlug, domainId }: ImportAuthorizationCodesModalProps) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [importMode, setImportMode] = useState<'skip' | 'update' | 'replace'>('skip');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setCsvContent(null);
      setRowCount(0);
      setResults(null);
      setError(null);
      setImportMode('skip');
      setIsProcessing(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError(t('authorizationCodes.import.invalidFileType'));
      return;
    }

    setSelectedFile(file);
    setError(null);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const content = loadEvent.target?.result as string;
      if (!content || !content.trim()) {
        setError(t('authorizationCodes.import.invalidFileFormat'));
        setCsvContent(null);
        return;
      }

      const lines = content.split('\n').filter((line) => line.trim());
      const count = lines.length > 1 ? lines.length - 1 : 0;

      setCsvContent(content);
      setRowCount(count);
    };
    reader.onerror = () => {
      setError(t('authorizationCodes.import.invalidCsv'));
      setCsvContent(null);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvContent) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('/api/telephony/authorization-codes/import'), {
        method: 'POST',
        headers: getApiHeaders(tenantSlug),
        body: JSON.stringify({
          csvContent,
          mode: importMode,
          domainId
        })
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || t('authorizationCodes.import.error'));
      }

      setResults(payload.data as ImportResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('authorizationCodes.import.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    if (results && (results.created > 0 || results.updated > 0)) {
      onSuccess();
    }

    setSelectedFile(null);
    setCsvContent(null);
    setRowCount(0);
    setResults(null);
    setError(null);
    setImportMode('skip');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {t('authorizationCodes.import.title')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!results && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('authorizationCodes.import.selectFile')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={handleBrowse}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('authorizationCodes.import.browse')}
                  </button>
                  {selectedFile && (
                    <span className="text-sm text-muted-foreground">{selectedFile.name}</span>
                  )}
                </div>
              </div>

              {csvContent && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        {t('authorizationCodes.import.fileInfo')}
                      </p>
                      <ul className="text-blue-700 dark:text-blue-300 space-y-1">
                        <li>� {t('authorizationCodes.import.totalItems')}: {rowCount}</li>
                        <li>� {t('authorizationCodes.import.fileFormat')}: CSV</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {csvContent && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">
                    {t('authorizationCodes.import.mode')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="skip"
                        checked={importMode === 'skip'}
                        onChange={(event) => setImportMode(event.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('authorizationCodes.import.modeSkip')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('authorizationCodes.import.modeSkipDesc')}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="update"
                        checked={importMode === 'update'}
                        onChange={(event) => setImportMode(event.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('authorizationCodes.import.modeUpdate')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('authorizationCodes.import.modeUpdateDesc')}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="replace"
                        checked={importMode === 'replace'}
                        onChange={(event) => setImportMode(event.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('authorizationCodes.import.modeReplace')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('authorizationCodes.import.modeReplaceDesc')}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-3 p-3 border border-destructive/40 bg-destructive/10 rounded-lg text-sm text-destructive">
                  <AlertCircle className="h-5 w-5 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}

          {results && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1 text-sm">
                    <p className="font-semibold text-green-900 dark:text-green-100">
                      {t('authorizationCodes.import.success')}
                    </p>
                    <p className="text-green-700 dark:text-green-300">
                      {results.created} {t('authorizationCodes.import.created')}, {results.updated} {t('authorizationCodes.import.updated')}
                      {results.skipped > 0 && `, ${results.skipped} ${t('authorizationCodes.import.skipped')}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-900 dark:text-green-100">
                      {t('authorizationCodes.import.created')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">{results.created}</div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {t('authorizationCodes.import.createdDesc')}
                  </div>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      {t('authorizationCodes.import.updated')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{results.updated}</div>
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {t('authorizationCodes.import.updatedDesc')}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Info className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('authorizationCodes.import.total')}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-200">{results.total}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t('authorizationCodes.import.totalDesc')}
                </div>
              </div>

              {results.errors.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <XCircle className="h-5 w-5" />
                    <span>{t('authorizationCodes.import.errorsCount', { count: results.errors.length })}</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-destructive/30 rounded-lg bg-destructive/5 p-3 text-xs text-destructive">
                    <ul className="space-y-1">
                      {results.errors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span>{t('authorizationCodes.import.footerHint')}</span>
          </div>
          {!results ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg border border-border hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                disabled={isProcessing}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleImport}
                disabled={!csvContent || isProcessing}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? t('authorizationCodes.import.processing') : t('authorizationCodes.import.import')}
              </button>
            </div>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


